import { PaymentMethodEnum, ProviderEnum } from '@/types/common';
import type { ICheckoutContext } from '@/types/context';
import { handleStripeCardPayment } from '@/payment/stripePaymentHandler';
import { handleBraintreeCardPayment } from '@/payment/braintreePaymentHandler';
import { handleAmazonPayPayment } from '@/payment/amazonPayPaymentHandler';
import { handleGooglePayPayment } from '@/payment/googlePayPaymentHandler';
import { handleApplePayPayment } from '@/payment/applePayPaymentHandler';
import { handleLinkPayPayment } from '@/payment/linkPayPaymentHandler';
import { handlePayPalPayment } from '@/payment/paypalPaymentHandler';
import { handleKlarnaPayment } from '@/payment/klarnaPaymentHandler';
import { handleIdealPayment } from '@/payment/idealPaymentHandler';
import { handleVenmoPayment } from '@/payment/venmoPaymentHandler';
import type {
  GooglePayPaymentParams,
  ApplePayPaymentParams,
  LinkPayPaymentParams,
  PayPalPaymentParams,
  AmazonPayPaymentParams,
  KlarnaPaymentParams,
  IdealPaymentParams,
  VenmoPaymentParams,
} from '@/types/paymentHandlers';
import { handleAdyenCardPayment, handleAdyenPayment } from '@/payment/adyenPaymentHandle';
import { handleNuveiCardPayment } from '@/payment/nuveiPaymentHandler';
import { handleNuveiGenericPayment } from '@/payment/nuveiGenericPaymentHandle';
import { handleGenericStripePayment } from '@/payment/genericStirpePaymentHandle';

type PaymentHandler = (
  checkout: ICheckoutContext,
  sendPaymentToServer: <TResponse = unknown>(params: unknown) => Promise<TResponse>,
  params?: unknown
) => Promise<unknown>;

type PaymentHandlers = {
  [key in PaymentMethodEnum]?: PaymentHandler;
};

class PaymentHandlerService {
  private handlers: PaymentHandlers = {};

  constructor() {
    this.initializeHandlers();
  }

  private initializeHandlers() {
    this.handlers = {
      [PaymentMethodEnum.CREDIT_CARD]: async (checkout, sendPaymentToServer, params) => {
        const cardPaymentSettings = checkout.paymentMethodsSettings.find(
          (pm) => pm.paymentMethodName === PaymentMethodEnum.CREDIT_CARD
        );

        if (cardPaymentSettings?.provider === ProviderEnum.STRIPE) {
          return await handleStripeCardPayment(checkout, sendPaymentToServer, params as Record<string, unknown>);
        } else if (cardPaymentSettings?.provider === ProviderEnum.BRAINTREE) {
          return await handleBraintreeCardPayment(checkout, sendPaymentToServer, params as Record<string, unknown>);
        } else if (cardPaymentSettings?.provider === ProviderEnum.ADYEN) {
          return await handleAdyenCardPayment(checkout, sendPaymentToServer, params as Record<string, unknown>);
        } else if (cardPaymentSettings?.provider === ProviderEnum.NUVEI) {
          return await handleNuveiCardPayment(checkout, sendPaymentToServer, params as Record<string, unknown>);
        }
      },
      [PaymentMethodEnum.AMAZON_PAY]: async (checkout, sendPaymentToServer, params) => {
        return await handleAmazonPayPayment(checkout, sendPaymentToServer, params as AmazonPayPaymentParams);
      },
      [PaymentMethodEnum.GOOGLE_PAY]: async (checkout, sendPaymentToServer, params) => {
        return await handleGooglePayPayment(checkout, sendPaymentToServer, params as GooglePayPaymentParams);
      },
      [PaymentMethodEnum.APPLE_PAY]: async (checkout, sendPaymentToServer, params) => {
        return await handleApplePayPayment(checkout, sendPaymentToServer, params as ApplePayPaymentParams);
      },
      [PaymentMethodEnum.LINK]: async (checkout, sendPaymentToServer, params) => {
        return await handleLinkPayPayment(checkout, sendPaymentToServer, params as LinkPayPaymentParams);
      },
      [PaymentMethodEnum.PAYPAL]: async (checkout, sendPaymentToServer, params) => {
        return await handlePayPalPayment(checkout, sendPaymentToServer, params as PayPalPaymentParams);
      },
      [PaymentMethodEnum.KLARNA]: async (checkout, sendPaymentToServer, params) => {
        return await handleKlarnaPayment(checkout, sendPaymentToServer, params as KlarnaPaymentParams);
      },
      [PaymentMethodEnum.IDEAL]: async (checkout, sendPaymentToServer, params) => {
        return await handleIdealPayment(checkout, sendPaymentToServer, params as IdealPaymentParams);
      },
      [PaymentMethodEnum.VENMO]: async (checkout, sendPaymentToServer, params) => {
        return await handleVenmoPayment(checkout, sendPaymentToServer, params as VenmoPaymentParams);
      },
      [PaymentMethodEnum.AFTERPAY]: async (checkout, sendPaymentToServer, params) => {
        return await handleGenericStripePayment(checkout, sendPaymentToServer, params as Record<string, string>);
      },
      [PaymentMethodEnum.PIX]: async (checkout, sendPaymentToServer, params) => {
        return await handleNuveiGenericPayment(checkout, sendPaymentToServer, params as Record<string, string>);
      },
      [PaymentMethodEnum.NEOSURF]: async (checkout, sendPaymentToServer, params) => {
        return await handleNuveiGenericPayment(checkout, sendPaymentToServer, params as Record<string, string>);
      },
      [PaymentMethodEnum.NUVEI_NEOSURF]: async (checkout, sendPaymentToServer, params) => {
        return await handleNuveiGenericPayment(checkout, sendPaymentToServer, params as Record<string, string>);
      },
      [PaymentMethodEnum.P24]: async (checkout, sendPaymentToServer, params) => {
        return await handleGenericStripePayment(checkout, sendPaymentToServer, params as Record<string, string>);
      },
      [PaymentMethodEnum.BLIK]: async (checkout, sendPaymentToServer, params) => {
        return await handleGenericStripePayment(checkout, sendPaymentToServer, params as Record<string, string>);
      },
      [PaymentMethodEnum.BLIK_CHECKOUT]: async (checkout, sendPaymentToServer, params) => {
        return await handleGenericStripePayment(checkout, sendPaymentToServer, params as Record<string, string>);
      },
      [PaymentMethodEnum.CASH_APP]: async (checkout, sendPaymentToServer, params) => {
        return await handleGenericStripePayment(checkout, sendPaymentToServer, params as Record<string, string>);
      },
      [PaymentMethodEnum.TRUSTLY]: async (checkout, sendPaymentToServer, params) => {
        return await handleNuveiGenericPayment(checkout, sendPaymentToServer, params as Record<string, string>);
      },
      [PaymentMethodEnum.EPS]: async (checkout, sendPaymentToServer, params) => {
        return await handleGenericStripePayment(checkout, sendPaymentToServer, params as Record<string, string>);
      },
      [PaymentMethodEnum.BANCONTACT]: async (checkout, sendPaymentToServer, params) => {
        return await handleGenericStripePayment(checkout, sendPaymentToServer, params as Record<string, string>);
      },
      [PaymentMethodEnum.ALIPAY]: async (checkout, sendPaymentToServer, params) => {
        return await handleAdyenPayment(checkout, sendPaymentToServer, params as Record<string, string>);
      },
      [PaymentMethodEnum.PAYME]: async (checkout, sendPaymentToServer, params) => {
        return await handleAdyenPayment(checkout, sendPaymentToServer, params as Record<string, string>);
      },
    };
  }

  async processPayment(
    paymentMethod: PaymentMethodEnum,
    checkout: ICheckoutContext,
    sendPaymentToServer: <TResponse = unknown>(params: unknown) => Promise<TResponse>,
    params?: unknown
  ): Promise<unknown> {
    const handler = this.handlers[paymentMethod];

    if (!handler) {
      throw new Error(`No handler found for payment method: ${paymentMethod}`);
    }

    return await handler(checkout, sendPaymentToServer, params);
  }
}

export const paymentHandler = new PaymentHandlerService();
